/**
 * The LEVEL EDITOR's Pixi canvas. It renders the working {@link LevelDocument} WYSIWYG by reusing the
 * SHIPPED procedural renderer ({@link createOfficeMap} with a layout derived from the doc), and draws an
 * editor OVERLAY above it — a grid, seat/door markers (the map doesn't draw those), the selection outline
 * and zone resize handles. It owns no networking and no game loop; it just renders the doc and turns
 * pointer gestures (pan / zoom / select / move / resize / place) into pure {@link editor-doc} mutations,
 * which it hands back to React via callbacks. React owns the document; the stage is a controlled view.
 *
 * Smoothness: a drag mutates the stage's local doc and coalesces a rebuild to one per animation frame
 * (rebuilding a ~12-room map per frame is cheap), then commits the final doc to React on pointer-up — so
 * dragging stays at 60fps and React only re-renders on a settled change.
 */
import { Application, Container, Graphics, Sprite } from 'pixi.js';
import {
  SIM,
  layoutFromLevel,
  worldTheme,
  type LevelDocument,
  type WorldZone,
} from '@duck/contracts';
// The renderer lives in the office feature (it takes the optional layout → WYSIWYG with the live game).
import { createOfficeMap } from '../office/render/office-map';
import {
  doorCenter,
  moveSelected,
  resizeZone,
  snap,
  type PlacementArm,
  type SelectionRef,
} from './editor-doc';

const W = SIM.WORLD_WIDTH;
const H = SIM.WORLD_HEIGHT;
const MIN_ZOOM = 0.2;
const MAX_ZOOM = 3;
/** Pixel radius around an anchor/handle that counts as a hit (in screen px, divided by zoom). */
const HIT_OBJECT = 34;
const HIT_SEAT = 22;
const HIT_DECOR = 30;
const HIT_DOOR = 18;
const HANDLE = 9;

export interface EditorStageCallbacks {
  onSelect: (sel: SelectionRef | null) => void;
  onMutate: (doc: LevelDocument) => void;
}

type DragMode =
  | { kind: 'none' }
  | { kind: 'pan'; lastX: number; lastY: number }
  | { kind: 'move'; sel: SelectionRef; grabDx: number; grabDy: number }
  | { kind: 'resize'; id: string; corner: Corner; fixedX: number; fixedY: number }
  | { kind: 'pending'; sel: SelectionRef; downX: number; downY: number }; // click-vs-drag pending

type Corner = 'nw' | 'ne' | 'sw' | 'se';

export class EditorStage {
  private app: Application | null = null;
  private readonly world = new Container();
  private readonly mapHolder = new Container();
  private readonly overlay = new Graphics();
  private currentMap: Container | null = null;
  private bakedSprite: Sprite | null = null;
  private baked = false;

  private doc: LevelDocument;
  private sel: SelectionRef | null = null;
  private grid = 20;
  private gridOn = true;
  private placement: PlacementArm | null = null;

  /** Camera: the world point shown at screen centre + the zoom. */
  private cam = { x: W / 2, y: H / 2, zoom: 1 };
  private drag: DragMode = { kind: 'none' };
  private redrawQueued = false;
  private destroyed = false;

  constructor(
    doc: LevelDocument,
    private readonly cb: EditorStageCallbacks,
  ) {
    this.doc = doc;
  }

  async init(container: HTMLElement): Promise<void> {
    const app = new Application();
    await app.init({
      resizeTo: container,
      antialias: true,
      background: 0x0b1020,
      resolution: window.devicePixelRatio || 1,
      autoDensity: true,
    });
    if (this.destroyed) {
      app.destroy(true);
      return;
    }
    this.app = app;
    container.appendChild(app.canvas);

    this.world.addChild(this.mapHolder);
    this.world.addChild(this.overlay);
    app.stage.addChild(this.world);

    const canvas = app.canvas;
    canvas.style.touchAction = 'none';
    canvas.addEventListener('pointerdown', this.onPointerDown);
    window.addEventListener('pointermove', this.onPointerMove);
    window.addEventListener('pointerup', this.onPointerUp);
    canvas.addEventListener('wheel', this.onWheel, { passive: false });

    this.fit();
    this.rebuild();
  }

  // ── Public, React-driven API ──────────────────────────────────────────────────────────────────

  setDoc(doc: LevelDocument): void {
    this.doc = doc;
    this.scheduleRebuild();
  }

  setSelection(sel: SelectionRef | null): void {
    this.sel = sel;
    this.drawOverlay();
  }

  setGrid(on: boolean): void {
    this.gridOn = on;
    this.drawOverlay();
  }

  setPlacement(p: PlacementArm | null): void {
    this.placement = p;
    if (this.app) this.app.canvas.style.cursor = p ? 'crosshair' : 'default';
  }

  /**
   * BAKE the current map to ONE backdrop sprite (ADR-0026 parity): flatten the whole live map Container
   * into a single GPU texture so the finished level draws in ONE call instead of hundreds of Graphics —
   * the optimization the live game applies when it hosts a level. Toggling off restores the editable map.
   * Returns the number of display objects collapsed into the sprite (for a friendly "N → 1" readout).
   */
  setBaked(on: boolean): number {
    if (on === this.baked || !this.app) return 0;
    this.baked = on;
    if (on) {
      if (!this.currentMap) return 0;
      const count = this.currentMap.children.length;
      const texture = this.app.renderer.generateTexture({ target: this.currentMap, antialias: true });
      const sprite = new Sprite(texture);
      this.mapHolder.removeChildren();
      this.currentMap.destroy({ children: true });
      this.currentMap = null;
      this.bakedSprite = sprite;
      this.mapHolder.addChild(sprite);
      this.overlay.visible = false;
      this.app.canvas.style.cursor = 'default';
      return count;
    }
    this.bakedSprite?.destroy({ texture: true });
    this.bakedSprite = null;
    this.overlay.visible = true;
    this.rebuild();
    return 0;
  }

  isBaked(): boolean {
    return this.baked;
  }

  /** Fit + centre the whole 1600×1200 world in the viewport. */
  fit(): void {
    const { w, h } = this.viewport();
    const zoom = Math.min(w / W, h / H) * 0.92;
    this.cam = { x: W / 2, y: H / 2, zoom: Number.isFinite(zoom) && zoom > 0 ? zoom : 1 };
    this.applyCamera();
    this.drawOverlay();
  }

  destroy(): void {
    this.destroyed = true;
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
    if (this.app) {
      this.app.canvas.removeEventListener('pointerdown', this.onPointerDown);
      this.app.canvas.removeEventListener('wheel', this.onWheel);
      this.app.destroy(true, { children: true });
      this.app = null;
    }
  }

  // ── Rendering ─────────────────────────────────────────────────────────────────────────────────

  private viewport(): { w: number; h: number } {
    const app = this.app;
    if (!app) return { w: 1, h: 1 };
    return { w: app.screen.width, h: app.screen.height };
  }

  private applyCamera(): void {
    const { w, h } = this.viewport();
    this.world.scale.set(this.cam.zoom);
    this.world.position.set(w / 2 - this.cam.x * this.cam.zoom, h / 2 - this.cam.y * this.cam.zoom);
  }

  private scheduleRebuild(): void {
    if (this.redrawQueued) return;
    this.redrawQueued = true;
    requestAnimationFrame(() => {
      this.redrawQueued = false;
      if (!this.destroyed) this.rebuild();
    });
  }

  /** Rebuild the procedural map from the current doc (WYSIWYG) + redraw the overlay. */
  private rebuild(): void {
    if (!this.app || this.baked) return;
    const next = createOfficeMap(worldTheme(this.doc.themeId), layoutFromLevel(this.doc));
    this.mapHolder.removeChildren();
    this.currentMap?.destroy({ children: true });
    this.currentMap = next;
    this.mapHolder.addChild(next);
    this.drawOverlay();
  }

  /** Draw the editor overlay: grid, seat/door markers, selection outline + resize handles. */
  private drawOverlay(): void {
    const g = this.overlay;
    g.clear();

    // World bounds frame.
    g.rect(0, 0, W, H).stroke({ color: 0x4a5a8a, width: 2, alpha: 0.6 });

    // Grid (drawn in world units so it stays put under pan/zoom).
    if (this.gridOn) {
      const step = this.grid * 5; // major lines every 5 cells, kept faint
      for (let x = 0; x <= W; x += step) g.moveTo(x, 0).lineTo(x, H);
      for (let y = 0; y <= H; y += step) g.moveTo(0, y).lineTo(W, y);
      g.stroke({ color: 0x9fb3e6, width: 1, alpha: 0.08 });
    }

    // Seat markers — the map doesn't draw seats, so the editor shows each anchor + its facing tick.
    for (const s of this.doc.seats) {
      const hit = this.sel?.type === 'seat' && this.sel.id === s.id;
      g.circle(s.x, s.y, 9).fill({ color: hit ? 0xffd24a : 0x6ad1ff, alpha: hit ? 0.95 : 0.7 });
      g.circle(s.x, s.y, 9).stroke({ color: 0x0b1020, width: 1.5, alpha: 0.8 });
      g.moveTo(s.x, s.y)
        .lineTo(s.x + Math.cos(s.facing) * 14, s.y + Math.sin(s.facing) * 14)
        .stroke({ color: 0x0b1020, width: 2, alpha: 0.8 });
    }

    // Door markers — a small diamond on the edge centre.
    const zoneById = new Map(this.doc.zones.map((z) => [z.id, z]));
    for (const d of this.doc.doors) {
      const z = zoneById.get(d.zoneId);
      if (!z) continue;
      const c = doorCenter(d, z);
      const hit = this.sel?.type === 'door' && this.sel.id === d.id;
      g.poly([c.x, c.y - 7, c.x + 7, c.y, c.x, c.y + 7, c.x - 7, c.y]).fill({
        color: hit ? 0xffd24a : 0x9be29b,
        alpha: 0.9,
      });
    }

    // Selection outline.
    if (this.sel) this.drawSelection(g);
  }

  private drawSelection(g: Graphics): void {
    const sel = this.sel!;
    if (sel.type === 'zone') {
      const z = this.doc.zones.find((i) => i.id === sel.id);
      if (!z) return;
      g.rect(z.x, z.y, z.width, z.height).stroke({ color: 0xffd24a, width: 2, alpha: 0.95 });
      for (const [cx, cy] of this.zoneCorners(z)) {
        g.rect(cx - HANDLE, cy - HANDLE, HANDLE * 2, HANDLE * 2).fill({ color: 0xffd24a, alpha: 0.95 });
        g.rect(cx - HANDLE, cy - HANDLE, HANDLE * 2, HANDLE * 2).stroke({ color: 0x0b1020, width: 1.5 });
      }
    } else if (sel.type === 'object') {
      const o = this.doc.objects.find((i) => i.id === sel.id);
      if (o) g.circle(o.x, o.y, 32).stroke({ color: 0xffd24a, width: 3, alpha: 0.95 });
    } else if (sel.type === 'seat') {
      const s = this.doc.seats.find((i) => i.id === sel.id);
      if (s) g.circle(s.x, s.y, 16).stroke({ color: 0xffd24a, width: 3, alpha: 0.95 });
    } else if (sel.type === 'decor') {
      const d = this.doc.decor.find((i) => i.id === sel.id);
      if (d) g.circle(d.x, d.y, 26).stroke({ color: 0xffd24a, width: 3, alpha: 0.95 });
    } else {
      const d = this.doc.doors.find((i) => i.id === sel.id);
      const z = d ? this.doc.zones.find((zz) => zz.id === d.zoneId) : undefined;
      if (d && z) {
        const c = doorCenter(d, z);
        g.circle(c.x, c.y, 14).stroke({ color: 0xffd24a, width: 3, alpha: 0.95 });
      }
    }
  }

  private zoneCorners(z: WorldZone): Array<[number, number, Corner]> {
    return [
      [z.x, z.y, 'nw'],
      [z.x + z.width, z.y, 'ne'],
      [z.x, z.y + z.height, 'sw'],
      [z.x + z.width, z.y + z.height, 'se'],
    ];
  }

  // ── Coordinate maths ──────────────────────────────────────────────────────────────────────────

  private toWorld(clientX: number, clientY: number): { x: number; y: number } {
    const rect = this.app!.canvas.getBoundingClientRect();
    const sx = clientX - rect.left;
    const sy = clientY - rect.top;
    return {
      x: (sx - this.world.position.x) / this.cam.zoom,
      y: (sy - this.world.position.y) / this.cam.zoom,
    };
  }

  // ── Hit testing ───────────────────────────────────────────────────────────────────────────────

  /** Resize-handle hit for the currently selected zone (so a corner grab beats a move). */
  private hitHandle(wx: number, wy: number): { id: string; corner: Corner } | null {
    if (this.sel?.type !== 'zone') return null;
    const z = this.doc.zones.find((i) => i.id === this.sel!.id);
    if (!z) return null;
    const r = HANDLE / this.cam.zoom + 2;
    for (const [cx, cy, corner] of this.zoneCorners(z)) {
      if (Math.abs(wx - cx) <= r && Math.abs(wy - cy) <= r) return { id: z.id, corner };
    }
    return null;
  }

  /** Topmost element under a world point. Priority: objects > seats > doors > (smallest) zone. */
  private hitItem(wx: number, wy: number): SelectionRef | null {
    const within = (px: number, dist: number): boolean => dist <= px / this.cam.zoom;

    let best: { sel: SelectionRef; d: number } | null = null;
    const consider = (sel: SelectionRef, d: number, px: number): void => {
      if (within(px, d) && (!best || d < best.d)) best = { sel, d };
    };

    for (const o of this.doc.objects) consider({ type: 'object', id: o.id }, Math.hypot(o.x - wx, o.y - wy), HIT_OBJECT);
    for (const s of this.doc.seats) consider({ type: 'seat', id: s.id }, Math.hypot(s.x - wx, s.y - wy), HIT_SEAT);
    for (const d of this.doc.decor) consider({ type: 'decor', id: d.id }, Math.hypot(d.x - wx, d.y - wy), HIT_DECOR);
    const zoneById = new Map(this.doc.zones.map((z) => [z.id, z]));
    for (const d of this.doc.doors) {
      const z = zoneById.get(d.zoneId);
      if (!z) continue;
      const c = doorCenter(d, z);
      consider({ type: 'door', id: d.id }, Math.hypot(c.x - wx, c.y - wy), HIT_DOOR);
    }
    if (best) return (best as { sel: SelectionRef }).sel;

    // Fall back to the smallest zone whose rect contains the point (inner rooms win over the hub).
    let zoneHit: { id: string; area: number } | null = null;
    for (const z of this.doc.zones) {
      if (wx >= z.x && wx < z.x + z.width && wy >= z.y && wy < z.y + z.height) {
        const area = z.width * z.height;
        if (!zoneHit || area < zoneHit.area) zoneHit = { id: z.id, area };
      }
    }
    return zoneHit ? { type: 'zone', id: zoneHit.id } : null;
  }

  // ── Pointer handlers ──────────────────────────────────────────────────────────────────────────

  private readonly onPointerDown = (e: PointerEvent): void => {
    if (!this.app) return;
    e.preventDefault();
    // While baked (the optimized single-sprite preview) only panning is allowed — editing is off.
    if (this.baked) {
      this.drag = { kind: 'pan', lastX: e.clientX, lastY: e.clientY };
      return;
    }
    const { x, y } = this.toWorld(e.clientX, e.clientY);

    // Placement mode: drop a new element at the snapped point.
    if (this.placement) {
      this.placeAt(snap(x, this.grid), snap(y, this.grid));
      return;
    }

    // Resize handle on the selected zone takes precedence.
    const handle = this.hitHandle(x, y);
    if (handle) {
      const z = this.doc.zones.find((i) => i.id === handle.id)!;
      const fixed = this.fixedCorner(z, handle.corner);
      this.drag = { kind: 'resize', id: handle.id, corner: handle.corner, fixedX: fixed.x, fixedY: fixed.y };
      return;
    }

    const hit = this.hitItem(x, y);
    if (hit) {
      // Select immediately; defer the move/click decision until the pointer actually moves.
      this.sel = hit;
      this.cb.onSelect(hit);
      const item = this.anchorOf(hit);
      this.drag = { kind: 'pending', sel: hit, downX: x, downY: y };
      // Stash grab offset so a move keeps the cursor's relative grip.
      this.pendingGrab = item ? { dx: x - item.x, dy: y - item.y } : { dx: 0, dy: 0 };
      this.drawOverlay();
      return;
    }

    // Empty space → deselect + start panning.
    if (this.sel) {
      this.sel = null;
      this.cb.onSelect(null);
    }
    this.drag = { kind: 'pan', lastX: e.clientX, lastY: e.clientY };
    this.drawOverlay();
  };

  private pendingGrab = { dx: 0, dy: 0 };

  private readonly onPointerMove = (e: PointerEvent): void => {
    if (!this.app || this.drag.kind === 'none') return;
    const { x, y } = this.toWorld(e.clientX, e.clientY);

    if (this.drag.kind === 'pan') {
      const dx = (e.clientX - this.drag.lastX) / this.cam.zoom;
      const dy = (e.clientY - this.drag.lastY) / this.cam.zoom;
      this.cam.x -= dx;
      this.cam.y -= dy;
      this.drag = { kind: 'pan', lastX: e.clientX, lastY: e.clientY };
      this.applyCamera();
      return;
    }

    if (this.drag.kind === 'pending') {
      // Promote to a move once the pointer has clearly moved (avoids nudging on a plain click).
      if (Math.hypot(x - this.drag.downX, y - this.drag.downY) * this.cam.zoom > 4) {
        this.drag = { kind: 'move', sel: this.drag.sel, grabDx: this.pendingGrab.dx, grabDy: this.pendingGrab.dy };
      } else {
        return;
      }
    }

    if (this.drag.kind === 'move') {
      const nx = snap(x - this.drag.grabDx, this.grid);
      const ny = snap(y - this.drag.grabDy, this.grid);
      this.doc = moveSelected(this.doc, this.drag.sel, nx, ny);
      this.scheduleRebuild();
      return;
    }

    if (this.drag.kind === 'resize') {
      const nx = snap(x, this.grid);
      const ny = snap(y, this.grid);
      const x0 = Math.min(nx, this.drag.fixedX);
      const y0 = Math.min(ny, this.drag.fixedY);
      const w = Math.abs(nx - this.drag.fixedX);
      const h = Math.abs(ny - this.drag.fixedY);
      this.doc = resizeZone(this.doc, this.drag.id, x0, y0, w, h);
      this.scheduleRebuild();
    }
  };

  private readonly onPointerUp = (): void => {
    const wasEditing = this.drag.kind === 'move' || this.drag.kind === 'resize';
    this.drag = { kind: 'none' };
    if (wasEditing) this.cb.onMutate(this.doc); // commit the settled geometry to React
  };

  private readonly onWheel = (e: WheelEvent): void => {
    if (!this.app) return;
    e.preventDefault();
    const before = this.toWorld(e.clientX, e.clientY);
    const factor = e.deltaY < 0 ? 1.1 : 1 / 1.1;
    this.cam.zoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, this.cam.zoom * factor));
    this.applyCamera();
    // Keep the world point under the cursor fixed (zoom toward the pointer).
    const after = this.toWorld(e.clientX, e.clientY);
    this.cam.x += before.x - after.x;
    this.cam.y += before.y - after.y;
    this.applyCamera();
    this.drawOverlay();
  };

  // ── Placement + small helpers ─────────────────────────────────────────────────────────────────

  private placeAt(x: number, y: number): void {
    const p = this.placement;
    if (!p) return;
    // The PAGE owns id minting + selection, so the stage just reports the armed placement + the snapped
    // world point; the page performs the add (via editor-doc) and feeds the new doc back through setDoc.
    this.onPlace?.(p, x, y);
  }

  /** Set by the page to receive placement clicks (keeps id/selection ownership in React). */
  onPlace: ((arm: PlacementArm, x: number, y: number) => void) | null = null;

  private anchorOf(sel: SelectionRef): { x: number; y: number } | null {
    switch (sel.type) {
      case 'object': {
        const o = this.doc.objects.find((i) => i.id === sel.id);
        return o ? { x: o.x, y: o.y } : null;
      }
      case 'seat': {
        const s = this.doc.seats.find((i) => i.id === sel.id);
        return s ? { x: s.x, y: s.y } : null;
      }
      case 'decor': {
        const d = this.doc.decor.find((i) => i.id === sel.id);
        return d ? { x: d.x, y: d.y } : null;
      }
      case 'zone': {
        const z = this.doc.zones.find((i) => i.id === sel.id);
        return z ? { x: z.x, y: z.y } : null;
      }
      case 'door': {
        const d = this.doc.doors.find((i) => i.id === sel.id);
        const z = d ? this.doc.zones.find((zz) => zz.id === d.zoneId) : undefined;
        return d && z ? doorCenter(d, z) : null;
      }
    }
  }

  /** The corner diagonally OPPOSITE the grabbed one (stays fixed during a resize). */
  private fixedCorner(z: WorldZone, corner: Corner): { x: number; y: number } {
    const left = z.x;
    const right = z.x + z.width;
    const top = z.y;
    const bottom = z.y + z.height;
    switch (corner) {
      case 'nw':
        return { x: right, y: bottom };
      case 'ne':
        return { x: left, y: bottom };
      case 'sw':
        return { x: right, y: top };
      default:
        return { x: left, y: top };
    }
  }
}
